// LoginIntro.tsx

import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Chip, Stack, Card, CardContent, Avatar } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CheckCircleOutlineOutlined, QuizOutlined, Refresh } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { AML_KYC_QUIZ_DATA } from "../pages/LoginTestPage";

export const LoginIntro: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const questionKeys = useMemo(() => Object.keys(AML_KYC_QUIZ_DATA), []);
  const getRandomKey = () => questionKeys[Math.floor(Math.random() * questionKeys.length)];
  const [currentKey, setCurrentKey] = useState<string>(getRandomKey);
  const activeQuestion = AML_KYC_QUIZ_DATA[currentKey];

  return (
    <Box sx={{ position: "relative" }}>
      {/* Background Decorative Accent */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          right: "-10%",
          width: 450,
          height: 450,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.background.default, 0)} 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* Brand Header */}
      <Box>
        <Stack direction="row" sx={{ mb: 4, alignItems: "center", gap: 1.5 }}>
          <Avatar
            src="/logo.png"
            alt="ComplianceQuizHK"
            sx={{
              width: 44,
              height: 44,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5, color: "common.white" }}>
            {t("components.loginIntro.brandTitle")}
          </Typography>
        </Stack>

        {/* Hero Pitch */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            lineHeight: 1.2,
            mb: 2,
            fontSize: { xs: "2rem", sm: "2.5rem", lg: "3rem" },
            color: "common.white",
          }}
        >
          {t("components.loginIntro.heroTitle")}
        </Typography>
        <Typography variant="body1" sx={{ color: alpha(theme.palette.common.white, 0.75), mb: 4, maxWidth: 540 }}>
          {t("components.loginIntro.heroDescription")}
        </Typography>

        {/* Sector Badges */}
        <Stack direction="row" sx={{ mb: 5, flexWrap: "wrap", gap: 1 }}>
          {[
            t("components.loginIntro.topics.amlKyc"),
            t("components.loginIntro.topics.gdprDataPrivacy"),
            t("components.loginIntro.topics.soc2Cyber"),
            t("components.loginIntro.topics.hipaa"),
            t("components.loginIntro.topics.finraSec"),
            t("components.loginIntro.topics.esgOsha"),
          ].map((topic) => (
            <Chip
              key={topic}
              label={topic}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.08),
                color: "common.white",
                border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
                backdropFilter: "blur(6px)",
                fontWeight: 500,
              }}
            />
          ))}
        </Stack>

        {/* Interactive Mock Question Card Preview */}
        <Card
          elevation={0}
          sx={{
            maxWidth: 520,
            bgcolor: alpha(theme.palette.common.white, 0.06),
            backdropFilter: "blur(12px)",
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            borderRadius: 2,
            p: 1,
          }}
        >
          <CardContent>
            <Stack direction="row" sx={{ mb: 1.5, alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                <QuizOutlined sx={{ color: theme.palette.primary.light, fontSize: 20 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.light,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {t("components.loginIntro.challengeHeader", { category: activeQuestion.category })}
                </Typography>
              </Stack>

              <Button
                size="small"
                onClick={() => {
                  let nextKey = getRandomKey();
                  if (questionKeys.length > 1 && nextKey === currentKey) {
                    nextKey = questionKeys.find((k) => k !== currentKey) || nextKey;
                  }
                  setCurrentKey(nextKey);
                }}
                startIcon={<Refresh sx={{ fontSize: 14 }} />}
                sx={{
                  color: theme.palette.primary.light,
                  fontSize: "0.7rem",
                  textTransform: "none",
                  p: 0.5,
                  minWidth: "auto",
                }}
              >
                {t("components.loginIntro.shuffleButton")}
              </Button>
            </Stack>

            <Typography variant="body2" sx={{ color: "common.white", fontWeight: 600, mb: 2 }}>
              {t("components.loginIntro.questionPrefix", { question: activeQuestion.question })}
            </Typography>

            <Stack sx={{ gap: 1 }}>
              {activeQuestion.options.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    bgcolor: item.correct ? alpha(theme.palette.primary.light, 0.18) : alpha(theme.palette.common.white, 0.04),
                    border: item.correct ? `1px solid ${theme.palette.primary.light}` : "1px solid transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: item.correct ? theme.palette.primary.light : alpha(theme.palette.common.white, 0.7),
                      fontWeight: item.correct ? 700 : 400,
                    }}
                  >
                    {item.label}. {item.text}
                  </Typography>
                  {item.correct && <CheckCircleOutlineOutlined sx={{ color: theme.palette.primary.light, fontSize: 16 }} />}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Live Platform Stats */}
      <Stack direction="row" sx={{ mt: 6, pt: 3, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, gap: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.light }}>
            50,000+
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
            {t("components.loginIntro.stats.verifiedMcqs")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.light }}>
            2026
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
            {t("components.loginIntro.stats.updatedRules")}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.light }}>
            98.4%
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
            {t("components.loginIntro.stats.auditPassRate")}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
